interface SurveyQuestion {
  id: number | string
  text: string
  type: string
  isRequired?: boolean
  maxSelections?: number
  modelPath?: string
  responses?: Array<{
    id: number
    text: string
    nestedQuestions?: SurveyQuestion[]
  }>
}

export interface SubmissionPayload {
  surveyQuestionId: number
  surveyQuestionResponseIds?: number[]
  responseValue?: string | null
  metadata?: Record<string, any>
  modelPath?: string
}

/**
 * Prepares a submission payload for a survey question based on its type and value.
 * Returns null if the question should not be submitted (e.g., freeFormSkip, empty values).
 *
 * @param question - The survey question
 * @param value - The answer value (type varies by question type)
 * @param skipValue - Optional skip value (for insuranceMembershipId questions)
 * @returns Submission payload or null if should not be submitted
 */
export const prepareSubmissionPayload = (
  question: SurveyQuestion,
  value: any,
  skipValue?: boolean,
  metadata?: Record<string, any>
): SubmissionPayload | null => {
  const questionId =
    typeof question.id === 'string' ? parseInt(question.id, 10) : question.id

  // Skip submission for freeFormSkip questions
  if (question.type === 'freeFormSkip') {
    return null
  }

  // Base payload with modelPath if available
  const basePayload: SubmissionPayload = {
    surveyQuestionId: questionId,
    modelPath: question.modelPath,
  }

  // Handle select types (select, singleSelect, dropdown)
  if (
    question.type === 'select' ||
    question.type === 'singleSelect' ||
    question.type === 'dropdown'
  ) {
    if (value === null || value === undefined || value === '') {
      return null
    }
    const responseId = typeof value === 'string' ? parseInt(value, 10) : value
    return {
      ...basePayload,
      surveyQuestionResponseIds: [responseId],
    }
  }

  // Handle multiSelect
  if (question.type === 'multiSelect') {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return null
    }
    const responseIds = Array.isArray(value)
      ? value.map(v => (typeof v === 'string' ? parseInt(v, 10) : v))
      : [typeof value === 'string' ? parseInt(value, 10) : value]
    return {
      ...basePayload,
      surveyQuestionResponseIds: responseIds,
    }
  }

  // Handle boolean
  if (question.type === 'boolean') {
    return {
      ...basePayload,
      responseValue:
        value === true || value === '1' || value === 'true' ? '1' : '0',
    }
  }

  // Handle text types (text, longText)
  if (question.type === 'text' || question.type === 'longText') {
    if (!value || value === '') {
      return null
    }
    return {
      ...basePayload,
      responseValue: String(value),
    }
  }

  // Handle date (stored in YYYY-MM-DD format)
  if (question.type === 'date') {
    if (!value || value === '') {
      return null
    }
    return {
      ...basePayload,
      responseValue: String(value),
    }
  }

  // Handle heightPicker (total inches as string)
  if (question.type === 'heightPicker') {
    if (value === null || value === undefined || value === '') {
      return null
    }
    return {
      ...basePayload,
      responseValue: String(value),
    }
  }

  // Handle weightPicker (weight in lbs as string)
  if (question.type === 'weightPicker') {
    if (value === null || value === undefined || value === '') {
      return null
    }
    return {
      ...basePayload,
      responseValue: String(value),
    }
  }

  // Handle insuranceMembershipId (string or null if skipped)
  if (question.type === 'insuranceMembershipId') {
    // Check if skip checkbox is checked - if skipped, submit with null value and metadata
    if (skipValue === true) {
      return {
        ...basePayload,
        responseValue: null,
        metadata: {
          skipMembershipId: true,
        },
      }
    }
    // If no value and not skipped, don't submit (unless required, but validation handles that)
    if (value === null || value === undefined || value === '') {
      return null
    }
    return {
      ...basePayload,
      responseValue: String(value),
    }
  }

  // Handle custom question types
  if (question.type === 'custom') {
    // For payerSearch: value is payer ID string, metadata contains full payer object
    // For diagnosisSelect: value is comma-separated codes string
    if (value === null || value === undefined || value === '') {
      return null
    }

    // Check if this is a payer search question (by component name)
    const isPayerSearch = (question as any).component === 'payerSearch'

    // For payer search, include metadata if available
    if (isPayerSearch && metadata) {
      return {
        ...basePayload,
        responseValue: String(value),
        metadata,
      }
    }

    return {
      ...basePayload,
      responseValue: String(value),
    }
  }

  // Default: treat as responseValue string
  if (value === null || value === undefined || value === '') {
    return null
  }
  return {
    ...basePayload,
    responseValue: String(value),
  }
}
